import { AlertColor, Button, CircularProgress, Grid, Link, Modal, TextField, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import React, { useImperativeHandle } from 'react';
import { globalTheme } from '../../../common/theme';
import { SnackBarMessage, SnackBarMessageProps } from '../../component/SnackBarMessage';
import { useAppSelector } from '../../../common/hooks';
import { RootState } from '../../../common/Store';
import Title from '../../component/Title';
import { GeneralDialog } from '../../component/GeneralDialog';
import { renewToken } from '../../../services/AuthService';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        margin: {
            margin: theme.spacing(1),
        },
        modalPaper: {
            position: 'absolute',
            width: 400,
            backgroundColor: theme.palette.background.paper,
            border: '2px solid #000',
            boxShadow: theme.shadows[5],
            padding: theme.spacing(2, 4, 3),
        },
        control: {
            marginBottom: theme.spacing(1)
        }
    }),

);

export interface PurchaseOrderViewerHandle {
    Initialise: (poNo: string) => void,
}

export interface PurchaseOrderViewerProps {
    onClose: Function;
}
  
export const PurchaseOrderViewer = React.forwardRef((props: PurchaseOrderViewerProps, ref: React.Ref<PurchaseOrderViewerHandle>) => {
    const style = { ...useStyles(), ...globalTheme() };
    const isDebug = useAppSelector((state: RootState) => state.menuToggle.debugMode);
    const [open, setOpen] = React.useState(false);
    const [loading, setLoading] = React.useState<any>();
    const [poNo, setPoNo] = React.useState<string>();

    //Dialogs and snacks
    const hasChanges = React.useRef<boolean>(false);
    const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
    const [snackSettings, setSnackSettings] =
      React.useState<SnackBarMessageProps>({
        open: false,
        message: "",
        subMessage: "",
        severity: undefined,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });

    function showSnackBar(message: string, subMessage: string, colour?: AlertColor) {
      setSnackSettings({
        open: true,
        message: message,
        subMessage: subMessage,
        severity: colour,
        onClose: () => setSnackSettings({ open: false, message: "", subMessage: "", severity: undefined, onClose: () => {}})
      });
    }


    const [viewerUrl, setViewerUrl] = React.useState<string>("");

    useImperativeHandle(ref, () => ({

        Initialise(poNo: string) {
            renewToken();
            setOpen(true);
            setLoading(true);
            hasChanges.current = false;

            GenerateReport(poNo);
            setPoNo(poNo);
        }

    }));

    function Export(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, p: string) {
        const params = { poNo: p };
        axios.get(`Api/V1/PurchaseOrder/Export`, {
            params,
            headers: {
                'Content-Encoding': 'gzip'
            },
            responseType: 'blob'
        })
            .then((result) => {
                const file = new Blob([result.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;" });
  
                const url = window.URL.createObjectURL(file);
  
                const link = document.createElement("a");
                document.body.appendChild(link);
                link.href = url;
                link.download = p + ".xls";
                link.click();
  
                setLoading(false);
            },
                (error) => {
                    console.log(error);
                    setLoading(false);
                    showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                }
            )
      }
  
    function CloseModal() {
        setDialogOpen(false)
        setOpen(false);
    }

    function GenerateReport(p: string) {
        const params = { poNo: p };
        axios.get(`Api/V1/PurchaseOrder/Preview`, {
            params,
            headers: {
                'Content-Encoding': 'gzip'
            },
            responseType: 'blob'
        })
            .then((result) => {
                const file = new Blob([result.data], { type: "application/pdf" });

                const url = window.URL.createObjectURL(file);

                setViewerUrl(url);
                setLoading(false);
            },
                (error) => {
                    console.log(error);
                    setLoading(false);
                    showSnackBar(error.toString(), error?.response?.data?.errors[0], "error");
                }
            )
    }

    return (
        <div>
            <Modal
                open={open}
             >
                <div className={style.ModalMain}>

                    {!loading ? (
                        <div style={{ height: '100%' }}>
                            <Title pageTitle={poNo ?? ""} />

                            <div className={style.ModalHeading}>
                                <Typography variant="h4">{poNo}</Typography>
                            </div>

                            <div className={style.ModalContent} style={{height: '75vh'}}>
                                <iframe src={viewerUrl} width="100%" height="100%" style={{overflow: 'auto'}}>
                                </iframe>
                            </div>

                            <div className={style.ModalFooter}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    className={style.margin}
                                    onClick={(e) => Export(e, poNo ?? "")}>Export</Button>
                                <Button
                                    variant="contained"
                                    className={style.margin}
                                    onClick={(e) => CloseModal()}>Close</Button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center' }}>
                            <CircularProgress color="primary" />
                        </div>
                    )}



                </div>
            </Modal>

            <SnackBarMessage {...snackSettings} />

            <GeneralDialog title={"Save Changes?"} message={"Changes have been made, did you want to save before closing?"} open={dialogOpen} options={
                [
                    { text: 'No', action: (event) => CloseModal(), color: "primary", variant: "contained" }
                ]
            } />
        </div >
    );
});
